import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BaseDetailData, SelectedCustomerService } from 'gung-standard';
import { JeevesProductDetailsComponent } from 'gung-standard-jeeves';
import { filter, first, forkJoin, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { TyloPriceConfigService } from '../../services/tylo-price-config.service';
import { TyloConceptProductListConfigService } from '../../services/tylo-concept-product-list-config.service';
import { environment } from './../../../environments/environment';

// gung-current-language is replaced with the current language code
// const productDetailsTable: string[] = [
//   'extra.pim.eNumber',
//   'extra.pim.width',
//   'extra.ar.artbtotvikt',
//   'extra.ar.artvikt',
//   'extra.pim.height',
//   'extra.pim.depth',
//   'extra.pim.dimension',
//   'extra.pim.countryOfOrigin',
//   'extra.pim.voltage',
//   'extra.pim.room_size',
//   'extra.pim.power',
//   'extra.pim.effect_W',
//   'extra.pim.safetyDistanceWall',
//   'extra.pim.safetyDistanceCeiling',
//   'extra.i18n.gung-current-language.stone',
//   'extra.i18n.gung-current-language.controlPanel',
//   'extra.i18n.gung-current-language.relayBox',
//   'extra.i18n.gung-current-language.installation',
//   'extra.i18n.gung-current-language.color',
//   'extra.i18n.gung-current-language.wood',
//   'extra.pim.benchSlat_mm',
//   'extra.pim.length',
//   'extra.pim.ledOutlets',
//   'extra.pim.benchWidth',
//   'extra.pim.slat',
//   'extra.i18n.gung-current-language.handle',
//   'extra.pim.hinge',
//   'extra.gung-current-language.se.material',
//   'extra.pim.frameDimensionWHD',
//   'extra.i18n.gung-current-language.profile',
//   'extra.pim.ipClass',
//   'extra.pim.steamProduction',
//   'extra.i18n.gung-current-language.glass',
//   'extra.pim.maxLoad',
//   'extra.i18n.gung-current-language.threshold',
//   'extra.i18n.gung-current-language.hingeSide',
//   'extra.pim.maxLoad_W',
//   'extra.i18n.gung-current-language.transportInfo',
//   'extra.pim.arQMSArVolt1',
//   'extra.pim.arQMSArVolt2',
//   'extra.pim.arQMSArVolt3'
// ];

const productDetailsTableHelo: string[] = [
  'extra.ar.q_ms_ar_color', // PIM
  'extra.ar.q_ms_ar_kw',
  'extra.ar.q_ms_ar_rsize',
  'extra.ar.q_ms_ar_ssize',
  'extra.ar.artvikt',
  'extra.ar.artbtotvikt',
  'extra.ar.q_ms_artlength',
  'extra.ar.q_ms_artheight',
  'extra.ar.q_ms_artwidth',
  'extra.ar.q_ar_langd',
  'extra.ar.q_ar_hojd',
  'extra.ar.q_ar_bredd',
  'extra.ar.q_ms_ar_sd1',
  'extra.ar.q_ms_ar_sd2',
  'extra.ar.q_ms_ar_sd3',
  'extra.ar.q_ms_ar_sd4',
  'extra.ar.q_ms_ar_sd5',
  'extra.ar.q_ms_ar_sd6',
  'extra.ar.q_ms_ar_volt1', // PIM
  'extra.ar.q_ms_ar_volt2', // PIM
  'extra.ar.q_ms_ar_volt3', // PIM
  'extra.ar.q_ms_ar_volt4',
  // 'extra.ar.artstatnr',
  'extra.ar.q_ar_sstl',
  // 'extra.ar.artnrean',
  'extra.ar.ursprungsland',
  'extra.ar.artnrean'
];

const voltageRealData = {
  1: '400-415V 3N~',
  2: '230-240V 1N/2~',
  3: '230V 3~',
  4: '400V 3~',
  5: '440V 3~',
  6: '240V 1 ph',
  7: '240V 3 ph',
  8: '120V 1 ph',
  9: '208V 3ph',
  10: '220V 3~',
  11: '440V 3N~',
  12: '400V-415V 2N~',
  13: '200V-240V 1N~/2~',
  14: '230V 1ph/240V 1ph',
  15: '230V 1ph'
};

@Component({
    selector: 'tylo-product-detail',
    templateUrl: './tylo-product-detail.component.html',
    styleUrls: ['./tylo-product-detail.component.css'],
    standalone: false,
    providers: [TyloConceptProductListConfigService]
})
export class TyloProductDetailComponent extends JeevesProductDetailsComponent implements OnInit, OnDestroy {
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected priceConfig = inject(TyloPriceConfigService);
  public conceptProductListConfig = inject(TyloConceptProductListConfigService);

  isTylo = environment.projectName === 'tylo';
  conceptsIds = [];
  productDetailsTable: { title: string; value: string }[] = [];
  isNoPrice = false;
  currentFlow;
  public pimSparePartsProductsIds: string[] = [];

  protected unsubscribe: Subject<void> = new Subject();
  showB2CPrices: boolean = false;

  ngOnInit() {
    super.ngOnInit();
    this.authService
      .getCurrentUser()
      .pipe(
        filter(user => !!user),
        first()
      )
      .subscribe(user => (this.isNoPrice = user.activeGroups.NOPRICE));
    this.gungFlowService.getSelectedFlow().pipe(first()).subscribe(f => this.currentFlow = f);
    this.priceConfig.showB2CPrices.pipe(takeUntil(this.unsubscribe)).subscribe(showB2CPrices => this.showB2CPrices = showB2CPrices);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  getProductDescription(): Observable<string> {
    const lang = this.translateService.currentLang || 'se';
    let description = '';
    if (this.product.extra.i18n && this.product.extra.i18n[lang]) {
      if (this.product.productType === 'concept' && this.product.extra.i18n[lang].conceptDescription) {
        description = this.product.extra.i18n[lang].conceptDescription;
      }
      else {
        description = this.product.extra.i18n[lang].longDescription || '';
      }
    }

    return of(description);
  }

  getDocumentName(document: any): string {
    if (!!document.s3Uri) {
      return document.s3Uri.split('/').reverse()[0];
    }

    return '';
  }

  protected handleObservable(observable: Observable<BaseDetailData>): void {
    observable.pipe(
      switchMap(d => forkJoin({
        product: of(d.product),
        availability: of(d.availability),
        price: of(d.price),
        user: of(d.user),
        flow: of(d.flow),
        customerPriceMarkup: this.selectedCustomerService.getSelectedCustomer().pipe(
          first(),
          switchMap(selectedCustomer => this.priceConfig.getB2CPriceMarkup(selectedCustomer.id).pipe(first()))
        )
      }))
    ).subscribe(d => {
      const { product, availability, price, customerPriceMarkup } = d;
      if (!!customerPriceMarkup) {
        if (!price.extra) {
          price.extra = {};
        }
        price.extra.b2cPrice = this.priceConfig.calculatePriceMarkup(customerPriceMarkup, price.customerGrossPrice?.value || 0);
      }
      if (product.extra.artv) {
        this.addonIds = product.extra.artv.map(r => r.artnrtillvalart);
      }
      if (product.extra.pim && product.extra.pim.relatedProducts) {
        this.pimRelatedProductsIds = product.extra.pim.relatedProducts.split(',');
      }
      const auxProduct = product as any;
      if (auxProduct.productType === 'concept' && auxProduct?.subProductIds) {
        this.conceptsIds = auxProduct.subProductIds;
      }

      if (product.extra.pim && product.extra.pim.spareparts) {
        this.pimSparePartsProductsIds = product.extra?.pim?.spareparts.split(',');
      }
      this.product = product;
      this.availability = availability;
      this.price = price;

      this.initImagesCarousel();
      if (this.isTylo) {
        // this.productDetailsTable = productDetailsTable
        //   .map(detail => this.getProductDetailTableTylo(detail))
        //   .filter(detail => !!detail);
       this.getTemplate();
      } else {
        this.productDetailsTable = productDetailsTableHelo
          .map(detail => this.getProductDetailTableTylo(detail))
          .filter(detail => !!detail);
      }
    });
  }


  getProductDetailTableTylo(fieldsStr: string): { title: string; value: string } {
    const lang = this.translateService.currentLang || 'se';
    const fields = fieldsStr.replace('gung-current-language', lang).split('.');

    if (!fields || fields.length === 0) {
      return;
    }

    const getField = (obje: object, field: string) => {
      if (!obje.hasOwnProperty(field)) {
        return null;
      }
      return obje[field];
    };

    let obj: any = Object.assign({}, this.product);
    for (const field of fields) {
      if (!obj) {
        break;
      }
      obj = getField(obj, field);
    }

    if (obj) {
      if (fields[fields.length - 1].includes('q_ms_ar_volt')) {
        return {
          title: fields[fields.length - 1],
          value: voltageRealData[obj]
        };
      }
      if (typeof obj === 'string') {
        return {
          title: fields[fields.length - 1],
          value: obj
        };
      }
      if (typeof obj === 'number') {
        return {
          title: fields[fields.length - 1],
          value: obj + ''
        };
      }
      if (obj.hasOwnProperty('description')) {
        // cehck if object have description
        return {
          title: fields[fields.length - 1],
          value: obj.description
        };
      }
    }

    return;
  }
}
